'use client';

import { type ComponentProps } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { SearchIcon } from 'lucide-react';

import { cn } from '@/util/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/Dialog/Dialog';

function Command({
    label,
    shouldFilter,
    filter,
    defaultValue,
    value,
    onValueChange,
    loop,
    disablePointerSelection,
    vimBindings,
    className,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof CommandPrimitive>) {
    return (
        <CommandPrimitive
            data-slot="command"
            className={cn(
                'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
                className,
            )}
            label={label}
            shouldFilter={shouldFilter}
            filter={filter}
            defaultValue={defaultValue}
            value={value}
            onValueChange={onValueChange}
            loop={loop}
            disablePointerSelection={disablePointerSelection}
            vimBindings={vimBindings}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

interface CommandDialogProps extends ComponentProps<typeof Dialog> {
    title?: string;
    description?: string;
    className?: string;
    showCloseButton?: boolean;
}

function CommandDialog({
    title = 'Command Palette',
    description = 'Search for a command to run...',
    children,
    className,
    showCloseButton = true,
    open,
    defaultOpen,
    modal,
    ...props
}: CommandDialogProps) {
    return (
        <Dialog open={open} defaultOpen={defaultOpen} modal={modal} {...props}>
            <DialogHeader className="sr-only">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <DialogContent
                className={cn('overflow-hidden p-0', className)}
                showCloseButton={showCloseButton}
            >
                <Command className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
                    {children}
                </Command>
            </DialogContent>
        </Dialog>
    );
}

interface CommandInputProps extends ComponentProps<typeof CommandPrimitive.Input> {
    wrapperClassname?: string;
}
function CommandInput({
    value,
    onValueChange,
    wrapperClassname,
    asChild,
    ref,
    className,
    ...props
}: CommandInputProps) {
    return (
        <div
            data-slot="command-input-wrapper"
            className={cn(
                'flex items-center gap-2 border-b bg-transparent px-3 py-2.5',
                wrapperClassname,
            )}
        >
            <SearchIcon className="text-muted-foreground size-4 shrink-0" />
            <CommandPrimitive.Input
                data-slot="command-input"
                className={cn(
                    'placeholder:text-muted-foreground flex w-full rounded-md bg-transparent text-sm font-normal tracking-normal outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
                    className,
                )}
                value={value}
                onValueChange={onValueChange}
                asChild={asChild}
                ref={ref}
                {...props}
            />
        </div>
    );
}

function CommandList({
    label,
    asChild,
    ref,
    className,
    ...props
}: ComponentProps<typeof CommandPrimitive.List>) {
    return (
        <CommandPrimitive.List
            data-slot="command-list"
            label={label}
            asChild={asChild}
            ref={ref}
            className={cn('scroll-py-1 overflow-x-hidden overflow-y-auto', className)}
            {...props}
        />
    );
}

function CommandEmpty({ asChild, ref, ...props }: ComponentProps<typeof CommandPrimitive.Empty>) {
    return (
        <CommandPrimitive.Empty
            data-slot="command-empty"
            asChild={asChild}
            ref={ref}
            className="text-muted-foreground px-2 py-1.5 text-sm font-normal tracking-normal"
            {...props}
        />
    );
}

function CommandGroup({
    heading,
    value,
    forceMount,
    asChild,
    ref,
    className,
    ...props
}: ComponentProps<typeof CommandPrimitive.Group>) {
    return (
        <CommandPrimitive.Group
            data-slot="command-group"
            heading={heading}
            value={value}
            forceMount={forceMount}
            asChild={asChild}
            ref={ref}
            className={cn(
                'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
                className,
            )}
            {...props}
        />
    );
}

function CommandSeparator({
    alwaysRender,
    asChild,
    ref,
    className,
    ...props
}: ComponentProps<typeof CommandPrimitive.Separator>) {
    return (
        <CommandPrimitive.Separator
            data-slot="command-separator"
            alwaysRender={alwaysRender}
            asChild={asChild}
            ref={ref}
            className={cn('bg-border -mx-1 h-px', className)}
            {...props}
        />
    );
}

function CommandItem({
    disabled,
    onSelect,
    value,
    keywords,
    forceMount,
    asChild,
    ref,
    className,
    ...props
}: ComponentProps<typeof CommandPrimitive.Item>) {
    return (
        <CommandPrimitive.Item
            data-slot="command-item"
            disabled={disabled}
            onSelect={onSelect}
            value={value}
            keywords={keywords}
            forceMount={forceMount}
            asChild={asChild}
            ref={ref}
            className={cn(
                "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-normal tracking-normal outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                'cursor-pointer gap-2 rounded-sm px-2 py-1.5 pointer-coarse:min-h-11',
                className,
            )}
            {...props}
        />
    );
}

function CommandShortcut({ className, ref, ...props }: ComponentProps<'span'>) {
    return (
        <span
            data-slot="command-shortcut"
            className={cn(
                'text-muted-foreground ml-auto text-xs font-normal tracking-normal',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}

export {
    Command,
    CommandDialog,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandShortcut,
    CommandSeparator,
};
